import { Injectable, inject } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { StorageService } from "./storage.service";

/**
 * Represents the result of an HTTP operation,
 * including both the response status code and the deserialized body.
 */
export interface HttpResult<T> {
  /** HTTP status code returned by the server. */
  status: number;
  /** Parsed response body of type `T`. */
  body: T;
}

/**
 * Generic HTTP service that wraps Angular's HttpClient to provide
 * typed results, automatic authorization headers, and query parameter handling.
 */

@Injectable({ providedIn: "root" })
export class BaseHttpService {
  /** Angular HttpClient, injected via `inject`. */
  protected http = inject(HttpClient);
  /** StorageService for retrieving stored tokens when `useAuth` is true. */
  protected storage = inject(StorageService);

  /**
   * Performs an HTTP GET request.
   *
   * @template T  Expected type of the response body.
   * @param url            URL endpoint to call.
   * @param queryParams    Key/value pairs to append as query parameters.
   * @param useAuth        If true, adds an Authorization header with the stored ID token.
   * @param customHeaders  Any additional headers to send (overrides default Content-Type).
   * @returns Observable emitting an object with `status` and typed `body`.
   */

  protected get<T>(
    url: string,
    queryParams: Record<string, any> = {},
    useAuth = false,
    customHeaders: Record<string, string> = {}
  ): Observable<HttpResult<T>> {

    // Add skip-auth header when useAuth = false
    if (!useAuth) {
      customHeaders['skip-auth'] = 'true';
    }
    
    const options = {
      headers: this._createHeaders(useAuth, customHeaders),
      params: this._buildHttpParams(queryParams),
      observe: "response" as const,
    };
    return this.http.get<T>(url, options).pipe(
      map((resp: HttpResponse<T>) => ({
        status: resp.status,
        body: resp.body!,
      }))
    );
  }

  /**
   * Performs an HTTP POST request.
   *
   * @template T  Expected type of the response body.
   * @param url            URL endpoint to call.
   * @param body           Payload to send in the request body.
   * @param queryParams    Key/value pairs to append as query parameters.
   * @param useAuth        If true, adds an Authorization header with the stored ID token.
   * @param customHeaders  Any additional headers to send (overrides default Content-Type).
   * @returns Observable emitting an object with `status` and typed `body`.
   */

  protected post<T>(
    url: string,
    body?: any,
    queryParams: Record<string, any> = {},
    useAuth = false,
    customHeaders: Record<string, string> = {}
  ): Observable<HttpResult<T>> {
    const options = {
      headers: this._createHeaders(useAuth, customHeaders),
      params: this._buildHttpParams(queryParams),
      observe: "response" as const,
    };
    return this.http.post<T>(url, body, options).pipe(
      map((resp: HttpResponse<T>) => ({
        status: resp.status,
        body: resp.body!,
      }))
    );
  }

  /**
   * Performs an HTTP PUT request.
   *
   * @template T  Expected type of the response body.
   * @param url            URL endpoint to call.
   * @param body           Payload to send in the request body.
   * @param queryParams    Key/value pairs to append as query parameters.
   * @param useAuth        If true, adds an Authorization header with the stored ID token.
   * @param customHeaders  Any additional headers to send (overrides default Content-Type).
   * @returns Observable emitting an object with `status` and typed `body`.
   */
  protected put<T>(
    url: string,
    body: any,
    queryParams: Record<string, any> = {},
    useAuth = false,
    customHeaders: Record<string, string> = {}
  ): Observable<HttpResult<T>> {
    const options = {
      headers: this._createHeaders(useAuth, customHeaders),
      params: this._buildHttpParams(queryParams),
      observe: "response" as const,
    };
    return this.http.put<T>(url, body, options).pipe(
      map((resp: HttpResponse<T>) => ({
        status: resp.status,
        body: resp.body!,
      }))
    );
  }

  /**
   * Performs an HTTP DELETE request.
   *
   * @template T  Expected type of the response body.
   * @param url            URL endpoint to call.
   * @param queryParams    Key/value pairs to append as query parameters.
   * @param useAuth        If true, adds an Authorization header with the stored ID token.
   * @param customHeaders  Any additional headers to send (overrides default Content-Type).
   * @returns Observable emitting an object with `status` and typed `body`.
   */
  protected delete<T>(
    url: string,
    queryParams: Record<string, any> = {},
    useAuth = false,
    customHeaders: Record<string, string> = {}
  ): Observable<HttpResult<T>> {
    const options = {
      headers: this._createHeaders(useAuth, customHeaders),
      params: this._buildHttpParams(queryParams),
      observe: "response" as const,
    };
    return this.http.delete<T>(url, options).pipe(
      map((resp: HttpResponse<T>) => ({
        status: resp.status,
        body: resp.body!,
      }))
    );
  }

  /**
   * Performs an HTTP PATCH request.
   *
   * @template T  Expected type of the response body.
   * @param url            URL endpoint to call.
   * @param body           Payload to send in the request body.
   * @param queryParams    Key/value pairs to append as query parameters.
   * @param useAuth        If true, adds an Authorization header with the stored ID token.
   * @param customHeaders  Any additional headers to send (overrides default Content-Type).
   * @returns Observable emitting an object with `status` and typed `body`.
   */
  protected patch<T>(
    url: string,
    body: any,
    queryParams: Record<string, any> = {},
    useAuth = false,
    customHeaders: Record<string, string> = {}
  ): Observable<HttpResult<T>> {
    const options = {
      headers: this._createHeaders(useAuth, customHeaders),
      params: this._buildHttpParams(queryParams),
      observe: "response" as const,
    };
    return this.http.patch<T>(url, body, options).pipe(
      map((resp: HttpResponse<T>) => ({
        status: resp.status,
        body: resp.body!,
      }))
    );
  }

  /**
   * Builds HttpHeaders for a request.
   * Always includes a default Content-Type header (unless overridden),
   * and adds an Authorization header if `useAuth` is true and a token is available.
   *
   * @param useAuth        Whether to include the `Authorization` header.
   * @param customHeaders  Custom header key/value pairs (overrides defaults).
   * @returns Configured HttpHeaders instance.
   */

  private _createHeaders(
    useAuth: boolean,
    customHeaders: Record<string, string> = {}
  ): HttpHeaders {
    let headers = new HttpHeaders(
      Object.keys(customHeaders).length
        ? customHeaders
        : { "Content-Type": "application/json" }
    );

    if (useAuth) {
      const token = this.storage.getIdToken();
      if (token) {
        headers = headers.set("Authorization", token);
      }
    }
    return headers;
  }

  /**
   * Converts a plain object into HttpParams, excluding `null` or `undefined` values.
   * Supports array values by appending multiple entries for the same key.
   *
   * @param params  Key/value pairs to transform.
   * @returns HttpParams instance with all provided parameters set.
   */
  private _buildHttpParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            httpParams = httpParams.append(key, v.toString());
          });
        } else {
          httpParams = httpParams.set(key, value.toString());
        }
      }
    });
    return httpParams;
  }


  /**
   * Performs a POST request to an S3 bucket.
   *
   * @template T  Expected type of the response body.
   * @param url  URL endpoint for the S3 bucket.
   * @param body Payload to send in the request body.
   * @returns Observable emitting an object with `status` and typed `body`.
   */
  protected s3BucketPost<T>(
    url: string,
    body: any,
  ): Observable<HttpResult<T>> {
    return this.http.post<T>(url, body, {
       observe: 'response' as const,
       headers: new HttpHeaders({ 'skip-auth': 'true' }) // tell interceptor to skip auth
     }).pipe(
      map((resp: HttpResponse<T>) => ({
        status: resp.status,
        body: resp.body!,
      }))
    );
  }

}
